import {FC, JSX, ReactElement, useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'

import {UserQueries} from '../../api/user'
import {useVenue} from '../../api/venue/hooks'
import {AuthAgreement} from '../../components/legal/auth-agreement'
import {CheckEmailForm} from '../../services/auth/forms/check-email-form'
import {SignInForm} from '../../services/auth/forms/sign-in-form'
import {isNilOrEmpty, isNotNilAndNotEmpty} from '../../utils/logic'
import {CheckEmailStep} from './check-email-step'
import {PhoneLoginStep} from './phone-login-step'
import {SignInStep} from './sign-in-step'
import {SignUpStep} from './sign-up-step/sign-up-step'
import {ModalParams} from './types'

type Props = {
  email?: string
  venueId?: string
  setModalTitle?: (title: ReactElement | string) => void
}

export type Step = 'check_email' | 'phone_login' | 'sign_in' | 'sign_up_free'

export const AuthModal: FC<Props> = ({email, venueId, setModalTitle}) => {
  const [step, setStep] = useState<Step>('check_email')
  const [params, setParams] = useState<ModalParams>({email})
  const [emailToUse, setEmailToUse] = useState<string | undefined>(email)
  const {data: venue} = useVenue()
  const {data, isLoading, isRefetching, refetch} = useQuery(
    UserQueries.getUserDataByEmailQuery({
      query: {
        email: emailToUse,
      },
      enabled: isNotNilAndNotEmpty(emailToUse),
    }),
  )

  const handleNavigateToStep = useCallback((stepToSet: Step, paramsToSet?: ModalParams) => {
    setStep(stepToSet)
    if (paramsToSet) {
      setParams(paramsToSet)
    }
  }, [])

  const setEmail = useCallback(
    (mail: string) => {
      setEmailToUse(mail)
      refetch()
    },
    [setEmailToUse, refetch],
  )

  const steps = useMemo<{[key: string]: JSX.Element}>(
    () => ({
      check_email: (
        <CheckEmailForm
          initialEmail={emailToUse}
          setEmailToUse={setEmail}
          navigateToStep={handleNavigateToStep}
        >
          <CheckEmailStep
            isLoading={isLoading || isRefetching}
            setModalTitle={setModalTitle}
            formModalDescription={venue?.entityNames.registerModalDescription}
            phoneLoginAction={() => handleNavigateToStep('phone_login')}
          />
        </CheckEmailForm>
      ),
      phone_login: (
        <PhoneLoginStep
          venueId={venueId}
          backToEmail={(emailForLogin) => {
            if (emailForLogin) {
              setEmail(emailForLogin)
            }
            handleNavigateToStep('check_email')
          }}
        />
      ),
      sign_in: (
        <SignInForm
          email={emailToUse}
          venueId={venueId}
          userData={data}
          setModalTitle={setModalTitle}
        >
          <SignInStep
            backAction={() => handleNavigateToStep('check_email')}
            phoneLoginAction={() => handleNavigateToStep('phone_login')}
          />
        </SignInForm>
      ),
      sign_up_free: (
        <SignUpStep
          email={emailToUse}
          venueId={venueId}
          backAction={() => handleNavigateToStep('check_email')}
          setModalTitle={setModalTitle}
        />
      ),
    }),
    [handleNavigateToStep, params.email, isLoading, isRefetching],
  )

  useEffect(() => {
    if (isNilOrEmpty(emailToUse) || isLoading || isRefetching) {
      return
    }
    if (isNilOrEmpty(data)) {
      handleNavigateToStep('sign_up_free')
    } else {
      handleNavigateToStep('sign_in')
    }
  }, [data, isLoading, emailToUse, isRefetching])

  return (
    <>
      {steps[step]}
      <AuthAgreement />
    </>
  )
}
